import { Button, Divider, Stack } from '@mui/material';
import bgPostgraduate from '@/assets/images/png/postgraduate.png';
import {
  ResourceCard,
  ResourceCardContainer,
  ResourceCardContent,
  ResourceCardHeader,
  ResourceCardMetaItem,
  ResourceCardTitle,
} from '@/components/shared/resource-card';
import { courseTypesLabels } from '@/constants/catalog/course';
import { mapResourceType } from '@/constants/resources/map-resource-type';
import {
  courseTypeIconNames,
  externalResourceIconNames,
} from '@/constants/resources/resources-icons';
import { resourceImage } from '@/constants/resources/resources-images';
import type { ResourceGroup } from '@/data/learning-path/interfaces';
import { normalizeUrl } from '@/packages/learning-path/components/resource-item/normalize-url';
import { formatDurationWithModality } from '@/utils/format-duration-with-modality';
import { formatExternalInstitution } from '@/utils/format-external-institution';

interface ResourceItemProps {
  resource: ResourceGroup;
  onEdit?: () => void;
  onDelete?: () => void;
}

export const ResourceItem = ({
  resource,
  onEdit,
  onDelete,
}: ResourceItemProps) => {
  const hasActions = onEdit || onDelete;

  if (resource.course) {
    return (
      <ResourceCard
        href={
          (!hasActions &&
            normalizeUrl(resource?.course?.external_course_url)) ||
          undefined
        }
      >
        <ResourceCardHeader
          institution={formatExternalInstitution(resource.course)}
        />
        <ResourceCardContainer>
          <ResourceCardTitle>{resource.course.name}</ResourceCardTitle>
          <ResourceCardContent>
            <ResourceCardMetaItem
              icon={courseTypeIconNames[resource.course.course_type]}
            >
              {courseTypesLabels[resource.course.course_type]}
            </ResourceCardMetaItem>
            <ResourceCardMetaItem icon="AccessTime">
              {formatDurationWithModality(
                resource.course.duration,
                resource.course.duration_type,
                resource.course.modality
              )}
            </ResourceCardMetaItem>
          </ResourceCardContent>
        </ResourceCardContainer>
        {onDelete && (
          <>
            <Divider sx={{ mt: 'auto' }} />
            <Stack
              direction="row"
              justifyContent="space-between"
              p={1}
              spacing={1}
            >
              {onDelete && (
                <Button color="error" onClick={onDelete}>
                  Excluir
                </Button>
              )}
            </Stack>
          </>
        )}
      </ResourceCard>
    );
  }

  if (resource.external_link) {
    const categoryKey = mapResourceType[resource.external_link.category];
    const image = resourceImage[categoryKey];
    const icon = externalResourceIconNames[categoryKey];
    const coverImageSrc =
      resource.external_link.cover_image_url ||
      resource.external_link.cover_image?.previewUrl ||
      resource.external_link.cover_image?.blob ||
      image ||
      bgPostgraduate;

    return (
      <ResourceCard
        href={(!hasActions && resource.external_link.url) || undefined}
      >
        <ResourceCardHeader
          image={{
            src: coverImageSrc,
            alt: resource.external_link.category,
            type: 'image',
          }}
        />
        <ResourceCardContainer>
          <ResourceCardTitle>{resource.external_link.name}</ResourceCardTitle>
          <ResourceCardContent>
            <ResourceCardMetaItem icon={icon}>
              {resource.external_link.category}
            </ResourceCardMetaItem>
          </ResourceCardContent>
        </ResourceCardContainer>
        {(onEdit || onDelete) && (
          <>
            <Divider sx={{ mt: 'auto' }} />
            <Stack
              direction="row"
              justifyContent="space-between"
              p={1}
              spacing={1}
            >
              {onDelete && (
                <Button color="error" onClick={onDelete}>
                  Excluir
                </Button>
              )}
              {onEdit && (
                <Button variant="contained" onClick={onEdit}>
                  Editar
                </Button>
              )}
            </Stack>
          </>
        )}
      </ResourceCard>
    );
  }

  return null;
};
